import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Button,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemSecondaryAction,
    ListItemText,
    Paper,
    Theme,
    Typography
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useHistory } from 'react-router';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../ReduxStore';

import ssologinImage from '../../Images/ssologin.png';
import ZkiliRedirectsApi from '../../API/ZkiliRedirectsApi';
import useSnackbar from '../../Hooks/useSnackbar';
import copyToClipboard from '../../Functions/CopyTextToClipBoard';
import SEO from '../../Components/SEO';
import { fetchShips, fetchSystems } from '../../Slices/sdeSlice';

const useStyles = makeStyles((theme: Theme) => ({
    formPaper: {
        padding: theme.spacing(1)
    },
    title: {
        marginTop: theme.spacing(1),
        marginLeft: theme.spacing(2),
        marginRight: 'auto'
    },
    loginFirst: {
        textAlign: 'center'
    },
    loginImageLink: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        maxHeight: 42
    }
}));

const MAX_LINKS = 25;

const ZkillCustom = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const { openSnackbar } = useSnackbar();
    const user = useSelector((state: RootState) => state.user.loggedIn);
    const [customLinks, setCustomLinks] = useState([]);

    useEffect(() => {
        dispatch(fetchShips());
        dispatch(fetchSystems());
        ZkiliRedirectsApi.list()
            .then(result => {
                setCustomLinks(result);
            })
            .catch(error => {
                if (error?.response?.data?.error) {
                    openSnackbar(error?.response?.data?.error);
                } else {
                    openSnackbar('Failed to fetch your redirect links, something went wrong.');
                }
            });
    }, []);

    const copyLink = link => event => {
        event.stopPropagation();
        copyToClipboard(link);
        openSnackbar('Copied link to your clipboard.');
    };
    const onEditClick = (event, id) => {
        event.stopPropagation();
        history.push(`/zkili-links/custom/${id}`);
    };
    if (!user) {
        return (
            <Grid container spacing={1} justifyContent="center">
                <Grid item xs={12} md={6} className={classes.loginFirst}>
                    <Typography variant="h5">Login using your Eve account before continuing</Typography>
                    <a href={`${process.env.REACT_APP_API_BASE}/api/auth/eve-login`}>
                        <img className={classes.loginImageLink} src={ssologinImage} alt="Eve online sso button" />
                    </a>
                    <Typography>Links are connected to your Eve characters to edit them later</Typography>
                </Grid>
            </Grid>
        );
    }

    const list = (
        <List>
            {customLinks?.map(customLink => {
                const config = customLink.config;
                const primary = `${config.name} (${config.corporation}) lost their ${config.ship} in ${
                    config.system
                } worth ${parseFloat(String(config.value || 0)).toLocaleString('us', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })}`;

                return (
                    <ListItem key={customLink.id} onClick={e => onEditClick(e, customLink.id)}>
                        <ListItemButton>
                            <ListItemText
                                primary={primary}
                                secondary={`https://zkiliboard.com/kill/${customLink.linkId}/`}
                            />
                            <ListItemSecondaryAction>
                                <IconButton onClick={copyLink(`https://zkilllboard.com/kill/${customLink.linkId}/`)}>
                                    <ContentCopy />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItemButton>
                    </ListItem>
                );
            })}
        </List>
    );
    const canCreate = customLinks?.length < MAX_LINKS;

    return (
        <Grid container spacing={1} justifyContent="center">
            <SEO title="Custom Zkili Link" path="/zkili-links/custom" description="A list of all your redirect links" />

            <Grid item xs={12} md={6}>
                <Paper className={classes.formPaper}>
                    <Typography variant="h5" className={classes.title}>
                        Your links {customLinks.length} / {MAX_LINKS}
                    </Typography>

                    {customLinks?.length === 0 && (
                        <Box sx={{ textAlign: 'center', margin: 4 }}>
                            <Typography variant="h6">You have 0 custom links</Typography>
                        </Box>
                    )}

                    {list}

                    {canCreate && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant="outlined" size="small" component={Link} to="/zkili-links/custom/create">
                                Create
                            </Button>
                        </Box>
                    )}
                </Paper>
            </Grid>
        </Grid>
    );
};

export default ZkillCustom;
